import React, { useState } from 'react';
import { Zap, DollarSign, TrendingDown, TrendingUp, Thermometer, Users, Wind, AlertCircle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const EnergyManagement: React.FC = () => {
  const [automationRules, setAutomationRules] = useState([
    {
      id: 'rule1',
      condition: 'Occupancy < 10% for 30 minutes',
      action: 'Reduce HVAC power by 40%',
      enabled: true
    },
    {
      id: 'rule2',
      condition: 'Natural light > 800 lux',
      action: 'Dim artificial lighting to 30%',
      enabled: true
    }
  ]);

  const handleRuleToggle = (ruleId: string) => {
    setAutomationRules(rules => rules.map(rule => {
      if (rule.id === ruleId) {
        return { ...rule, enabled: !rule.enabled };
      }
      return rule;
    }));
  };

  const roomData = [
    {
      id: 'conf-a',
      name: 'Conference Room A',
      occupancy: 8,
      activePaths: 12,
      currentTemp: 72,
      targetTemp: 72,
      hvacStatus: 'Maintaining' as const,
      savings: 14.2,
      lastUpdate: '2 mins ago'
    },
    {
      id: 'conf-b',
      name: 'Conference Room B',
      occupancy: 0,
      activePaths: 0,
      currentTemp: 68,
      targetTemp: 60,
      hvacStatus: 'Off' as const,
      savings: 28.5,
      lastUpdate: '1 min ago'
    },
    {
      id: 'open-north',
      name: 'Open Office North',
      occupancy: 24,
      activePaths: 35,
      currentTemp: 71,
      targetTemp: 72,
      hvacStatus: 'Heating' as const,
      savings: 8.3,
      lastUpdate: 'Just now'
    },
    {
      id: 'open-south',
      name: 'Open Office South',
      occupancy: 18,
      activePaths: 22,
      currentTemp: 73,
      targetTemp: 72,
      hvacStatus: 'Cooling' as const,
      savings: 11.7,
      lastUpdate: '30 secs ago'
    }
  ];

  const savingsData = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, '0') + ':00';
    const baseline = 50 + Math.random() * 30;
    const actual = baseline - (baseline * (0.15 + Math.random() * 0.25));
    return {
      time: hour,
      baseline: Math.round(baseline),
      actual: Math.round(actual),
      savings: Math.round(baseline - actual)
    };
  });

  const emptyIntervals = [
    { hour: '18', occupied: false, hvacRunning: true, setbackEligible: true },
    { hour: '19', occupied: false, hvacRunning: true, setbackEligible: true },
    { hour: '20', occupied: false, hvacRunning: true, setbackEligible: true },
    { hour: '21', occupied: false, hvacRunning: false, setbackEligible: true },
    { hour: '22', occupied: false, hvacRunning: false, setbackEligible: true },
    { hour: '23', occupied: false, hvacRunning: false, setbackEligible: true },
    { hour: '00', occupied: false, hvacRunning: false, setbackEligible: true },
    { hour: '01', occupied: false, hvacRunning: false, setbackEligible: true },
    { hour: '02', occupied: false, hvacRunning: false, setbackEligible: true },
    { hour: '03', occupied: false, hvacRunning: false, setbackEligible: true },
    { hour: '04', occupied: false, hvacRunning: false, setbackEligible: true },
    { hour: '05', occupied: false, hvacRunning: false, setbackEligible: true },
    { hour: '06', occupied: false, hvacRunning: true, setbackEligible: true },
    { hour: '07', occupied: true, hvacRunning: true, setbackEligible: false },
    { hour: '08', occupied: true, hvacRunning: true, setbackEligible: false }
  ];

  const getHVACStatusColor = (status: string) => {
    switch (status) {
      case 'Cooling': return '#3b82f6';
      case 'Heating': return '#ef4444';
      case 'Maintaining': return '#0A9B9B';
      case 'Off': return '#666';
      default: return '#666';
    }
  };

  return (
    <div className="p-6 space-y-6" style={{ maxWidth: '1600px', margin: '0 auto' }}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--sca-ink)' }}>
          Energy Management
        </h2>
        <p style={{ color: 'var(--sca-muted)' }}>
          Floor sensor data updating in real-time. Sample building data, illustrative
        </p>
      </div>

      {/* Disclaimer */}
      <div className="rounded-lg border p-4" style={{ backgroundColor: '#fef9e7', borderColor: '#e8dfd0' }}>
        <div className="flex items-start gap-3">
          <AlertCircle size={18} style={{ color: '#8a7355', flexShrink: 0, marginTop: 2 }} />
          <div className="text-sm" style={{ color: '#8a7355' }}>
            <strong>Evidence for BMS:</strong> Floor sensors provide occupancy presence evidence. Building Management Systems (BMS) own HVAC control. 
            Sensor data informs setback eligibility; it does not replace BMS integration.
          </div>
        </div>
      </div>

      {/* Summary Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>Savings Today</span>
          </div>
          <div className="text-3xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
            $7.40
          </div>
          <div className="flex items-center text-sm" style={{ color: '#0A9B9B' }}>
            <TrendingUp size={14} className="mr-1" />
            <span>3.2% vs yesterday</span>
          </div>
        </div>

        <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>Kilowatt-Hours Saved Today</span>
          </div>
          <div className="text-3xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
            46.28
          </div>
          <div className="flex items-center text-sm" style={{ color: '#0A9B9B' }}>
            <TrendingUp size={14} className="mr-1" />
            <span>2.1% vs yesterday</span>
          </div>
        </div>

        <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>Tons of CO2 Saved Today</span>
          </div>
          <div className="text-3xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
            0.03
          </div>
          <div className="flex items-center text-sm" style={{ color: 'var(--sca-muted)' }}>
            <TrendingDown size={14} className="mr-1" />
            <span>0% vs yesterday</span>
          </div>
        </div>
      </div>

      {/* Zone HVAC from Occupancy */}
      <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
          Zone HVAC from Occupancy
        </h3>
        <p className="text-sm mb-4" style={{ color: 'var(--sca-muted)' }}>
          Room-level HVAC adjusts to floor presence. Empty zones enter setback, reducing energy waste.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {roomData.map(room => (
            <div key={room.id} className="rounded-lg p-4 border" style={{ backgroundColor: 'var(--sca-hover)', borderColor: 'var(--sca-border)' }}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="font-semibold" style={{ color: 'var(--sca-ink)' }}>{room.name}</h4>
                  <div className="text-xs mt-1" style={{ color: 'var(--sca-muted)' }}>
                    Updated {room.lastUpdate}
                  </div>
                </div>
                <div className="text-xs px-2 py-1 rounded" style={{
                  backgroundColor: room.occupancy === 0 ? 'var(--sca-border)' : 'var(--sca-badge-bg)',
                  color: room.occupancy === 0 ? 'var(--sca-muted)' : 'var(--sca-teal)'
                }}>
                  {room.hvacStatus}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <Users size={14} style={{ color: 'var(--sca-muted)' }} />
                    <span className="text-xs" style={{ color: 'var(--sca-muted)' }}>Occupants</span>
                  </div>
                  <div className="font-semibold" style={{ color: 'var(--sca-ink)' }}>{room.occupancy}</div>
                </div>
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <Wind size={14} style={{ color: 'var(--sca-muted)' }} />
                    <span className="text-xs" style={{ color: 'var(--sca-muted)' }}>Active Paths</span>
                  </div>
                  <div className="font-semibold" style={{ color: 'var(--sca-ink)' }}>{room.activePaths}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <Thermometer size={14} style={{ color: 'var(--sca-muted)' }} />
                    <span className="text-xs" style={{ color: 'var(--sca-muted)' }}>Temperature</span>
                  </div>
                  <div className="text-sm">
                    <span className="font-semibold" style={{ color: 'var(--sca-ink)' }}>
                      {room.currentTemp}°F
                    </span>
                    <span className="mx-1" style={{ color: 'var(--sca-muted)' }}>→</span>
                    <span style={{ color: 'var(--sca-muted)' }}>{room.targetTemp}°F</span>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <Zap size={14} style={{ color: '#0A9B9B' }} />
                    <span className="text-xs" style={{ color: 'var(--sca-muted)' }}>Savings</span>
                  </div>
                  <div className="font-semibold" style={{ color: '#0A9B9B' }}>
                    {room.savings.toFixed(1)}%
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Real-time Energy Usage (Baseline vs Optimized) */}
      <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
          Real-time Energy Usage
        </h3>
        <p className="text-sm mb-4" style={{ color: 'var(--sca-muted)' }}>
          Occupancy-optimized load (teal) vs baseline always-on schedule (gray). Savings from empty zone setbacks.
        </p>

        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={savingsData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8e4dc" />
            <XAxis dataKey="time" stroke="#666" style={{ fontSize: '12px' }} />
            <YAxis stroke="#666" style={{ fontSize: '12px' }} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#F9F7F2', 
                border: '1px solid #e8e4dc',
                borderRadius: '8px'
              }} 
            />
            <Legend />
            <Area 
              type="monotone" 
              dataKey="baseline" 
              stroke="#94a3b8" 
              fill="#94a3b8" 
              fillOpacity={0.3} 
              name="Baseline" 
            />
            <Area 
              type="monotone" 
              dataKey="actual" 
              stroke="#0A9B9B" 
              fill="#0A9B9B" 
              fillOpacity={0.3} 
              name="Occupancy-Optimized" 
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Setback Eligibility Evidence (North Wing Detail) */}
      <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-1" style={{ color: 'var(--sca-ink)' }}>
            North Wing: Setback Eligibility Evidence
          </h3>
          <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>
            Floor presence shows zone empty 6 PM to 8 AM. Eligible setback hours: 14h overnight.
          </p>
        </div>

        <svg width="100%" height="250" viewBox="0 0 900 250" style={{ backgroundColor: '#F9F7F2', borderRadius: '8px' }}>
          {/* Grid lines */}
          <line x1="50" y1="50" x2="50" y2="220" stroke="#e8e4dc" strokeWidth="2" />
          <line x1="50" y1="220" x2="850" y2="220" stroke="#e8e4dc" strokeWidth="2" />

          {/* Hour labels */}
          {emptyIntervals.map((interval, idx) => {
            const x = 100 + idx * 50;
            return (
              <text
                key={idx}
                x={x}
                y="240"
                textAnchor="middle"
                style={{ fontSize: '10px', fill: '#666' }}
              >
                {interval.hour}
              </text>
            );
          })}

          {/* Business hours band */}
          <rect x="650" y="50" width="100" height="170" fill="#F9F7F2" opacity="0.5" />
          <text x="700" y="135" textAnchor="middle" style={{ fontSize: '11px', fill: '#999', fontWeight: '600' }}>
            Business Hours
          </text>

          {/* Occupancy band */}
          {emptyIntervals.map((interval, idx) => {
            const x = 100 + idx * 50;
            const height = interval.occupied ? 60 : 0;
            return (
              <rect
                key={`occ-${idx}`}
                x={x - 20}
                y={160 - height}
                width="40"
                height={height}
                fill={interval.occupied ? '#0A9B9B' : 'transparent'}
                opacity="0.6"
              />
            );
          })}

          {/* HVAC running indicators */}
          {emptyIntervals.map((interval, idx) => {
            const x = 100 + idx * 50;
            if (interval.hvacRunning) {
              return (
                <rect
                  key={`hvac-${idx}`}
                  x={x - 15}
                  y="165"
                  width="30"
                  height="50"
                  fill="#c33"
                  opacity="0.3"
                />
              );
            }
            return null;
          })}

          {/* Setback eligible band */}
          {emptyIntervals.map((interval, idx) => {
            const x = 100 + idx * 50;
            if (interval.setbackEligible && !interval.occupied) {
              return (
                <rect
                  key={`setback-${idx}`}
                  x={x - 20}
                  y="60"
                  width="40"
                  height="25"
                  fill="#0A9B9B"
                  opacity="0.8"
                />
              );
            }
            return null;
          })}

          {/* Legend */}
          <g transform="translate(100, 20)">
            <rect x="0" y="0" width="18" height="12" fill="#0A9B9B" opacity="0.6" />
            <text x="22" y="10" style={{ fontSize: '10px', fill: '#1a1a1a' }}>Occupied</text>

            <rect x="110" y="0" width="18" height="12" fill="#c33" opacity="0.3" />
            <text x="132" y="10" style={{ fontSize: '10px', fill: '#1a1a1a' }}>HVAC Running</text>

            <rect x="250" y="0" width="18" height="12" fill="#0A9B9B" opacity="0.8" />
            <text x="272" y="10" style={{ fontSize: '10px', fill: '#1a1a1a' }}>Setback Eligible</text>
          </g>
        </svg>

        <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: 'var(--sca-badge-bg)' }}>
          <div className="text-sm" style={{ color: 'var(--sca-ink)' }}>
            <strong>Eligible setback hours:</strong> 14 hours (6 PM to 8 AM). Zone empty with no floor presence. 
            BMS can reduce HVAC runtime by ~27% overnight vs baseline always-on schedule.
          </div>
        </div>
      </div>

      {/* Automation Rules */}
      <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
          Automation Rules
        </h3>
        <p className="text-sm mb-4" style={{ color: 'var(--sca-muted)' }}>
          Demo automation policies for occupancy-based HVAC and lighting control (illustrative).
        </p>

        <div className="space-y-4">
          {automationRules.map(rule => (
            <div key={rule.id} className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: 'var(--sca-hover)' }}>
              <div>
                <div className="font-medium mb-1" style={{ color: 'var(--sca-ink)' }}>{rule.condition}</div>
                <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>{rule.action}</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={rule.enabled}
                  onChange={() => handleRuleToggle(rule.id)}
                />
                <div className="w-11 h-6 rounded-full peer peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-offset-2 transition-colors"
                  style={{
                    backgroundColor: rule.enabled ? '#0A9B9B' : '#e8e4dc'
                  }}
                >
                  <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform"
                    style={{
                      transform: rule.enabled ? 'translateX(20px)' : 'translateX(0)'
                    }}
                  ></div>
                </div>
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EnergyManagement;
